import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { localizedPath, useLang } from "@/lib/i18n";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Copy, Sparkles, ClipboardCopy, Mail, Send, Share2 } from "lucide-react";
import { HtmlContent } from "@/components/RichText";
import ResultChart from "@/components/ResultChart";
import PercentRing from "@/components/PercentRing";
import ResultIllustration from "@/components/ResultIllustration";
import ShareSheet from "@/components/ShareSheet";
import NextTests from "@/components/NextTests";
import { FollowMindovia } from "@/components/SocialLinks";
import useSEO from "@/lib/useSEO";
import { toast } from "sonner";
import { useAccount } from "@/lib/AccountContext";
import { accountClaimErrorKind } from "@/lib/accountClaimFlow";
import {
  getPendingClaim,
  removePendingClaim,
  setAccountReturnIntent,
} from "@/lib/accountStorage";
import ResultSavePanel from "@/components/ResultSavePanel";
import { HollandCodeBadge, HollandProfileSection, hollandScoresInHexagonOrder } from "@/components/HollandProfile";
import { BigFiveHighlightBadge, BigFiveProfileSection } from "@/components/BigFiveProfile";
import { TypeCodeBadge, TypeCodeProfileSection } from "@/components/TypeCodeProfile";
import {
  getLearnMoreExtras,
  learnMoreLists,
  getLocalizedSafeResultContent,
  getResultAccessState,
  getResultPresentation,
  getSafeResultBlocks,
  hasRenderableResultContent,
} from "./resultPresentation";

function AiNarrativeCard({ code, lang, initialText, accessToken = "", preview = false }) {
  const [text, setText] = useState(initialText || "");
  const [loading, setLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ open: false, value: "", sending: false });
  const generate = async () => {
    if (text) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/public/results/${code}/ai-narrative`, null, {
        params: { lang, ...(accessToken ? { token: accessToken } : {}) },
      });
      setText(data.text || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setLoading(false); }
  };
  const copy = () => { navigator.clipboard.writeText(text); toast.success(lang === "id" ? "Narasi disalin" : "Narrative copied"); };
  const sendEmail = async () => {
    if (!emailForm.value || !/@/.test(emailForm.value)) { toast.error(lang === "id" ? "Email tidak valid" : "Invalid email"); return; }
    setEmailForm((p) => ({ ...p, sending: true }));
    try {
      const { data } = await api.post(`/public/results/${code}/email-narrative`, {
        email: emailForm.value,
        ...(accessToken ? { token: accessToken } : {}),
      }, { params: { lang } });
      if (data.ok) toast.success(lang === "id" ? "Narasi terkirim ke emailmu" : "Narrative sent to your email");
      else toast(lang === "id" ? "Permintaanmu tersimpan. Layanan email belum aktif, jadi tim kami akan mengirimkannya secara manual." : "Request saved. Email isn't set up yet, so our team will send it to you manually.");
      setEmailForm({ open: false, value: "", sending: false });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); setEmailForm((p) => ({ ...p, sending: false })); }
  };
  return (
    <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-terracotta-50 to-cream-100 border border-terracotta-200" data-testid="ai-narrative-card">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="w-9 h-9 rounded-full bg-terracotta-100 text-terracotta-700 flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg text-teal-800">{lang === "id" ? "Narasi Reflektif" : "Reflective Narrative"}</div>
          <div className="text-xs text-muted-foreground">{lang === "id" ? "Ringkasan reflektif dari skormu. Untuk eksplorasi diri, bukan diagnosis." : "A reflective summary based on your scores. For self-exploration, not a diagnosis."}</div>
        </div>
        {text && (
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-full" onClick={copy} data-testid="ai-copy-btn"><ClipboardCopy className="w-3.5 h-3.5 mr-1.5" /> {lang === "id" ? "Salin narasi" : "Copy narrative"}</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEmailForm((p) => ({ ...p, open: !p.open }))} data-testid="ai-email-toggle-btn"><Mail className="w-3.5 h-3.5 mr-1.5" /> {lang === "id" ? "Kirim ke email saya" : "Email me"}</Button>
          </div>
        )}
      </div>
      {!text && (
        <Button size="sm" onClick={generate} disabled={loading || preview} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid="ai-generate-btn">
          <Sparkles className="w-4 h-4 mr-2" /> {loading ? (lang === "id" ? "Menyiapkan…" : "Preparing…") : (lang === "id" ? "Buat Narasi Reflektif" : "Generate Reflective Narrative")}
        </Button>
      )}
      {text && (
        <div className="text-sm leading-relaxed whitespace-pre-wrap break-words text-teal-800/90" data-testid="ai-narrative-text">{text}</div>
      )}
      {emailForm.open && (
        <div className="mt-3 flex gap-2 items-center" data-testid="ai-email-form">
          <input type="email" value={emailForm.value} onChange={(e) => setEmailForm((p) => ({ ...p, value: e.target.value }))} placeholder="you@example.com" className="flex-1 h-9 rounded-full px-4 border border-border bg-background text-sm" data-testid="ai-email-input" />
          <Button size="sm" onClick={sendEmail} disabled={emailForm.sending} className="rounded-full bg-teal-800 hover:bg-teal-700 text-cream-50" data-testid="ai-email-send-btn"><Send className="w-3.5 h-3.5 mr-1.5" /> {emailForm.sending ? "…" : (lang === "id" ? "Kirim" : "Send")}</Button>
        </div>
      )}
    </div>
  );
}

// Admin preview: the share sheet is shown as it looks but stays inert (no result code exists).
function PreviewShareButton({ lang, compact = false }) {
  return compact ? (
    <Button variant="outline" size="icon" className="rounded-full" disabled aria-label={lang === "id" ? "Bagikan hasil" : "Share result"}>
      <Share2 className="w-4 h-4" />
    </Button>
  ) : (
    <Button className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" disabled>
      <Share2 className="w-4 h-4 mr-2" /> {lang === "id" ? "Bagikan Hasil" : "Share Result"}
    </Button>
  );
}

export const PREVIEW_RESULT_CODE = "PREVIEW";

function loadAccessToken(code) {
  if (typeof window === "undefined") return "";
  const urlToken = new URLSearchParams(window.location.search).get("token");
  if (urlToken) {
    try { localStorage.setItem(`mnd_pdf_token_${code}`, JSON.stringify({ token: urlToken })); } catch {}
    return urlToken;
  }
  try {
    const raw = localStorage.getItem(`mnd_pdf_token_${code}`);
    if (!raw) return "";
    try { return JSON.parse(raw)?.token || ""; }
    catch { return raw; }
  } catch { return ""; }
}

/**
 * Public result page. With `preview` ({ data, pdfMode }) it renders an admin preview payload
 * instead of fetching one: nothing is loaded, saved or paid for, and actions are inert.
 */
export default function Results({ preview = null }) {
  const params = useParams();
  const isPreview = !!preview;
  const code = isPreview ? PREVIEW_RESULT_CODE : params.code;
  const previewToken = preview?.data?.entitlement?.granted ? "preview" : "";
  const { t, lang, pick } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loading: accountLoading,
    isAuthenticated,
    claimResult,
    isResultSaved,
  } = useAccount();
  const [data, setData] = useState(() => preview?.data || null);
  const [loading, setLoading] = useState(!isPreview);
  const [pdfMode, setPdfMode] = useState(() => preview?.pdfMode || null);
  const [accessToken, setAccessToken] = useState(() => (isPreview ? previewToken : loadAccessToken(code)));
  const [checkingOut, setCheckingOut] = useState(false);
  const [pendingClaim, setPendingClaim] = useState(() => (isPreview ? null : getPendingClaim(code)));
  const [savingResult, setSavingResult] = useState(false);
  const accountClaimError = location.state?.accountClaimError || "";
  const [claimError, setClaimError] = useState(() => accountClaimError);

  useEffect(() => {
    if (!preview) return;
    setData(preview.data);
    setPdfMode(preview.pdfMode);
    setAccessToken(preview.data?.entitlement?.granted ? "preview" : "");
    setLoading(false);
  }, [preview]);

  // Load the saved token when navigating between result codes.
  useEffect(() => {
    if (isPreview) return;
    setAccessToken(loadAccessToken(code));
    setPendingClaim(getPendingClaim(code));
    setClaimError(accountClaimError);
  }, [accountClaimError, code, isPreview]);

  const beginAccountAuth = (path) => {
    setAccountReturnIntent({
      claimCode: code,
      returnTo: `/results/${encodeURIComponent(code)}`,
    });
    navigate(path);
  };

  const saveResult = async () => {
    if (!pendingClaim || savingResult) return;
    if (!isAuthenticated) {
      beginAccountAuth("/login");
      return;
    }

    setSavingResult(true);
    setClaimError("");
    try {
      await claimResult(code, pendingClaim.claimToken);
      removePendingClaim(code);
      setPendingClaim(null);
      toast.success(t("account.save.saved"));
    } catch (error) {
      const errorKind = accountClaimErrorKind(error);
      if (errorKind === "invalid") {
        removePendingClaim(code);
        setPendingClaim(null);
      }
      if (errorKind === "authentication") {
        setAccountReturnIntent({
          claimCode: code,
          returnTo: `/results/${encodeURIComponent(code)}`,
        });
        navigate("/login", { state: { returnTo: `/results/${encodeURIComponent(code)}` } });
      } else {
        setClaimError(errorKind);
      }
      toast.error(t(`account.claim.${errorKind}`));
    } finally {
      setSavingResult(false);
    }
  };

  // The backend shapes the payload from the entitlement token. Clear stale data
  // before every token transition so premium content from an earlier successful
  // request cannot remain visible.
  useEffect(() => {
    if (isPreview) return undefined;
    let active = true;
    setLoading(true);
    setData(null);
    api.get(`/public/results/${code}`, {
      params: { lang, ...(accessToken ? { token: accessToken } : {}) },
    }).then((response) => {
      if (active) setData(response.data);
    }).catch(() => {}).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [code, lang, accessToken, isPreview]);

  useEffect(() => {
    if (isPreview) return;
    api.get(`/public/results/${code}/pdf-mode`, { params: { lang } }).then(r => setPdfMode(r.data)).catch(() => setPdfMode({ mode: "free" }));
  }, [code, isPreview, lang]);

  // Validate stored token — if expired, clear it
  useEffect(() => {
    if (isPreview || !accessToken) return;
    api.get(`/public/results/${code}/access`, { params: { token: accessToken } })
      .then((r) => { if (!r.data.ok) { setData(null); setAccessToken(""); try { localStorage.removeItem(`mnd_pdf_token_${code}`); } catch {} } })
      .catch(() => {});
  }, [accessToken, code, isPreview]);

  const r = data?.result;
  const inst = data?.instrument;
  const template = inst?.result_template || {};
  const chartCfg = inst?.chart_config || {};
  const shareCfg = inst?.share_config || {};
  const mode = pdfMode?.mode || "free";
  const { isPaid, unlocked, paidContentLocked, premiumContentVisible } = getResultAccessState({
    mode,
    entitlement: data?.entitlement,
    accessToken,
  });

  const templateType = inst?.result_template_type || "profile"; // "profile" | "dimension" | "category"
  const isCategoryTemplate = templateType === "category";
  const categoryResult = r?.category_result || null;
  const isDominant = r?.method === "dominant_dimension" || (r?.rank_order && r.rank_order.length > 0);
  const isRankingMethod = r?.method === "ranking";
  const isCombinedMethod = r?.method === "combined_dimension";
  const combinedScores = r?.combined_scores || [];
  const primary = r?.primary_dimension || r?.dominant_dimension;
  const secondary = r?.secondary_dimension;
  const tie = r?.tie_status;

  // Track B — Universal Result Engine
  const mappingEntry = r?.result_mapping_entry || null;
  const layout = r?.result_layout || { show_hero: true, show_graph: true, show_ranking: false, show_dimensions: true, show_supporting: false, show_reflection: true, show_combined: false };
  const heroImg = mappingEntry ? (mappingEntry[`hero_image_${lang}_url`] || mappingEntry.hero_image_id_url || mappingEntry.hero_image_en_url || "") : "";
  const heroTitle = mappingEntry ? (mappingEntry[`title_${lang}`] || mappingEntry.title_id || mappingEntry.title_en || "") : "";
  const heroSubtitle = mappingEntry ? (mappingEntry[`subtitle_${lang}`] || mappingEntry.subtitle_id || mappingEntry.subtitle_en || "") : "";
  const heroReflection = mappingEntry ? (mappingEntry[`reflection_${lang}`] || mappingEntry.reflection_id || mappingEntry.reflection_en || "") : "";
  const heroColor = mappingEntry?.color || "";
  const { unresolvedGrouped, useUniversalHero, suppressLegacyHero } = getResultPresentation(r, layout);
  const { showHero: showSafeResultHero, showLearnMore: showSafeLearnMore } = getSafeResultBlocks(r, layout);
  const safePreview = getLocalizedSafeResultContent(r, lang);
  const hasHeroTitle = hasRenderableResultContent(heroTitle);
  const hasHeroSubtitle = hasRenderableResultContent(heroSubtitle);
  const hasHeroReflection = hasRenderableResultContent(heroReflection);
  const hasSafePreviewTitle = hasRenderableResultContent(safePreview.title);
  const hasSafePreviewSubtitle = hasRenderableResultContent(safePreview.subtitle);
  const hasSafePreviewBadge = hasRenderableResultContent(safePreview.badge);
  const hasSafePreviewImage = hasRenderableResultContent(safePreview.image);
  const hasSafePreviewText = hasSafePreviewBadge || hasSafePreviewSubtitle || hasSafePreviewTitle;
  // Holland (RIASEC) code + Learn More lists are premium detail: hidden on a locked paid preview.
  const holland = premiumContentVisible ? (r?.holland || null) : null;
  const showHollandHexagon = !!hollandScoresInHexagonOrder(holland);
  // Big Five trait map replaces the generic chart and dimension list; premium detail like Holland.
  const bigFive = premiumContentVisible ? (r?.big_five || null) : null;
  const showBigFiveMap = !!bigFive?.traits?.length && (layout.show_graph || layout.show_dimensions);
  // 16-type style letter code: same treatment, with a preference map.
  const typeCode = premiumContentVisible ? (r?.type_code || null) : null;
  const showTypeCodeMap = !!typeCode?.preferences?.length && (layout.show_graph || layout.show_dimensions);
  const ownProfileView = !!(bigFive || typeCode);
  const learnMoreExtras = paidContentLocked ? [] : getLearnMoreExtras(mappingEntry, lang, learnMoreLists(inst));
  const showLearnMore = paidContentLocked
    ? showSafeLearnMore
    : useUniversalHero
      && layout.show_reflection !== false
      && ([heroTitle, heroSubtitle, heroReflection].some(hasRenderableResultContent) || learnMoreExtras.length > 0);

  const copy = () => { navigator.clipboard.writeText(code); toast.success(lang === "id" ? "Kode disalin" : "Code copied"); };

  const startCheckout = async () => {
    setCheckingOut(true);
    const gateway = pdfMode?.active_gateway || "stripe";
    try {
      const origin = window.location.origin;
      if (gateway === "midtrans") {
        const { data } = await api.post(`/payments/midtrans/checkout`, { anonymous_code: code, origin_url: origin });
        if (!data?.snap_token) { toast.error("Snap token missing"); return; }
        // Dynamically load snap.js if not loaded
        const isProd = !!data.is_production;
        const snapSrc = isProd ? "https://app.midtrans.com/snap/snap.js" : "https://app.sandbox.midtrans.com/snap/snap.js";
        const clientKey = data.client_key || "";
        await new Promise((resolve, reject) => {
          if (window.snap) return resolve();
          const existing = document.querySelector('script[data-mindovia-snap]');
          if (existing) { existing.addEventListener("load", resolve); existing.addEventListener("error", reject); return; }
          const s = document.createElement("script");
          s.src = snapSrc;
          s.setAttribute("data-client-key", clientKey);
          s.setAttribute("data-mindovia-snap", "1");
          s.async = true;
          s.onload = resolve; s.onerror = reject;
          document.body.appendChild(s);
        }).catch(() => { throw new Error("Failed to load Snap.js"); });

        const finalize = async () => {
          try {
            const { data: st } = await api.get(`/payments/midtrans/status/${encodeURIComponent(data.order_id)}`);
            if (st.payment_status === "paid" && st.access_token) {
              setAccessToken(st.access_token);
              try { localStorage.setItem(`mnd_pdf_token_${code}`, JSON.stringify({ token: st.access_token })); } catch {}
              toast.success(pdfMode?.success_message || (lang === "id" ? "Pembayaran berhasil" : "Payment successful"));
            }
          } catch (e) { /* ignore */ }
        };

        window.snap.pay(data.snap_token, {
          onSuccess: finalize,
          onPending: () => { toast(lang === "id" ? "Menunggu konfirmasi pembayaran…" : "Awaiting payment confirmation…"); },
          onError: () => { toast.error(pdfMode?.failed_message || (lang === "id" ? "Pembayaran gagal" : "Payment failed")); },
          onClose: () => { finalize(); },
        });
      } else if (gateway === "paypal") {
        const { data } = await api.post(`/payments/paypal/checkout`, { anonymous_code: code, origin_url: origin });
        if (data.approve_url) window.location.href = data.approve_url;
        else toast.error("PayPal link missing");
      } else if (gateway === "stripe") {
        const { data } = await api.post(`/payments/checkout`, { anonymous_code: code, origin_url: origin });
        if (data.checkout_url) window.location.href = data.checkout_url;
        else toast.error("Checkout URL missing");
      } else {
        toast.error(lang === "id" ? "Pembayaran belum tersedia. Silakan hubungi kami." : "Payment isn't available right now. Please contact us.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setCheckingOut(false); }
  };

  const openPdf = () => {
    const url = `${window.location.origin}/api/public/results/${code}/pdf-paid?token=${accessToken}&lang=${lang}`;
    window.open(url, "_blank", "noopener");
  };

  useSEO(isPreview ? { url: null } : {
    title: lang === "id" ? "Hasil Refleksi — Mindovia" : "Reflection Result — Mindovia",
    description: lang === "id" ? "Hasil refleksi pribadi di Mindovia." : "A private reflection result on Mindovia.",
    url: null,
    robots: "noindex, nofollow, noarchive",
  });

  const chartDims = useMemo(() => {
    if (r?.rank_order?.length) return r.rank_order;
    return (r?.dimensions || []).map((d, i) => ({ ...d, rank: i + 1 }));
  }, [r]);

  if (loading) return <div className="py-20 text-center text-muted-foreground">…</div>;
  if (!data) return <div className="py-20 text-center">Hasil tidak ditemukan.</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14" data-wrap="responsive">
      <div className="bg-background p-2">
        {/* Page header */}
        <div className="mb-6 md:mb-8 flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-display text-3xl md:text-4xl tracking-tight leading-tight" data-testid="result-title">
              {lang === "id" ? "Profil Kamu" : "Your Profile"}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl text-sm md:text-base break-words">
              {(() => {
                const rd = inst?.result_display_config || {};
                const intro = lang === "id" ? (rd.intro_text_id || rd.intro_text_en) : (rd.intro_text_en || rd.intro_text_id);
                if (intro) return intro;
                return lang === "id"
                  ? `Berikut hasilmu untuk ${pick(inst || {}, "name") || "tes ini"}. Baca penjelasannya untuk mengenal dirimu lebih jauh.`
                  : `Here are your results for ${pick(inst || {}, "name") || "this test"}. Read on to learn more about yourself.`;
              })()}
            </p>
          </div>
          <div className="shrink-0">
            {isPreview ? <PreviewShareButton lang={lang} compact /> : (
              <ShareSheet
                code={code}
                instrument={inst}
                primary={primary}
                lang={lang}
                publicShareEnabled={!!shareCfg.public_share_enabled}
                accessToken={unlocked ? accessToken : ""}
                compact
              />
            )}
          </div>
        </div>

        {!isPreview && <ResultSavePanel
          accountLoading={accountLoading}
          isAuthenticated={isAuthenticated}
          hasPendingClaim={Boolean(pendingClaim)}
          saved={isResultSaved(code)}
          saving={savingResult}
          error={claimError ? t(`account.claim.${claimError}`) : ""}
          onSave={saveResult}
          onSignIn={() => beginAccountAuth("/login")}
          onRegister={() => beginAccountAuth("/account/register")}
        />}

        {/* Hero result card */}
        {paidContentLocked ? (
          showSafeResultHero ? (
            <section
              className="mb-8 p-5 md:p-8 rounded-3xl bg-card border border-border shadow-sm"
              style={safePreview.color ? { borderTopColor: safePreview.color, borderTopWidth: 4 } : undefined}
              data-testid="result-paid-preview"
            >
              <div className="grid md:grid-cols-5 gap-6 items-center">
                {hasSafePreviewText && (
                  <div className={hasSafePreviewImage ? "md:col-span-2" : "md:col-span-5"}>
                    {hasSafePreviewBadge && (
                      <span
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full uppercase tracking-widest"
                        style={{ background: (safePreview.color || "#c46b4f") + "22", color: safePreview.color || "#c46b4f" }}
                        data-testid="result-paid-preview-badge"
                      >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> {safePreview.badge}
                      </span>
                    )}
                    <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground">
                      {lang === "id" ? "Pratinjau hasil" : "Result preview"}
                    </div>
                    {hasSafePreviewSubtitle && (
                      <div className="mt-2 text-xs uppercase tracking-widest text-muted-foreground" data-testid="result-paid-preview-subtitle">
                        {safePreview.subtitle}
                      </div>
                    )}
                    {hasSafePreviewTitle && (
                      <h2 className="mt-2 font-display text-3xl md:text-4xl text-teal-800 break-words" data-testid="result-paid-preview-title">
                        {safePreview.title}
                      </h2>
                    )}
                    <p className="mt-3 text-sm text-muted-foreground">
                      {lang === "id" ? "Detail skor dan laporan lengkap bisa dilihat setelah pembayaranmu terkonfirmasi." : "Detailed scores and the full report unlock once your payment is confirmed."}
                    </p>
                  </div>
                )}
                {hasSafePreviewImage && (
                  <div className={hasSafePreviewText ? "md:col-span-3" : "md:col-span-5"}>
                    <img src={safePreview.image} alt={safePreview.title || "Result preview"} className="w-full h-auto max-h-[420px] object-contain rounded-2xl" data-testid="result-paid-preview-image" />
                  </div>
                )}
              </div>
              <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground" data-testid="result-anon-code">
                <span>{t("result.code")}:</span>
                <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/70 hover:bg-secondary font-mono text-[11px] tracking-wider" data-testid="result-code">
                  {code} <Copy className="w-3 h-3" />
                </button>
              </div>
            </section>
          ) : null
        ) : unresolvedGrouped ? (
          <section className="mb-8 p-5 md:p-8 rounded-3xl bg-card border border-border shadow-sm" data-testid="result-grouped-unresolved">
            <h2 className="font-display text-2xl text-teal-800">{lang === "id" ? "Hasil profil belum tersedia" : "Profile result is not available yet"}</h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              {lang === "id"
                ? "Hasil untuk kombinasi jawabanmu belum tersedia. Skormu tetap tersimpan, jadi silakan hubungi kami."
                : "A result for your combination of answers isn't available yet. Your scores are saved, so please get in touch with us."}
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground" data-testid="result-anon-code">
              <span>{t("result.code")}:</span>
              <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/70 hover:bg-secondary font-mono text-[11px] tracking-wider" data-testid="result-code">
                {code} <Copy className="w-3 h-3" />
              </button>
            </div>
          </section>
        ) : useUniversalHero ? (() => {
          const hasLearnMore = !!(heroTitle || heroSubtitle || heroReflection);
          return (
          <section
            className="mb-6 p-5 md:p-8 rounded-3xl bg-card border border-border shadow-sm relative overflow-hidden"
            style={heroColor ? { borderTopColor: heroColor, borderTopWidth: 4 } : undefined}
            data-testid="result-hero-card-universal"
            data-hero-layout="solo"
          >
            <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl" style={{ background: (heroColor || "rgb(212 122 101)") + "22" }} aria-hidden />
            {/* The hero image is a finished card that carries its own title, so it stands alone, centred. */}
            <div className="relative flex flex-col items-center gap-4 md:gap-6" data-testid="hero-layout-solo">
              {heroImg ? (
                <img
                  src={heroImg}
                  alt={heroTitle || "Hero"}
                  className="w-full max-w-3xl h-auto max-h-[640px] object-contain rounded-2xl mx-auto"
                  data-testid="result-hero-image"
                />
              ) : (
                <div className="w-full max-w-3xl aspect-square rounded-2xl bg-gradient-to-br from-terracotta-100 to-cream-100 flex items-center justify-center text-muted-foreground text-xs mx-auto">
                  {lang === "id" ? "Hero Card belum diunggah" : "No Hero Card uploaded"}
                </div>
              )}
              {holland && <HollandCodeBadge holland={holland} lang={lang} color={heroColor} />}
              {bigFive && <BigFiveHighlightBadge bigFive={bigFive} lang={lang} color={heroColor} />}
              {typeCode && <TypeCodeBadge typeCode={typeCode} lang={lang} color={heroColor} />}
            </div>
            <div className="relative mt-6 flex items-center gap-2 text-xs text-muted-foreground" data-testid="result-anon-code">
              <span>{t("result.code")}:</span>
              <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/70 hover:bg-secondary font-mono text-[11px] tracking-wider" data-testid="result-code">
                {code} <Copy className="w-3 h-3" />
              </button>
            </div>
          </section>
          );
        })() : !suppressLegacyHero && isCategoryTemplate && categoryResult ? (
          <section className="mb-8 p-5 md:p-8 rounded-3xl bg-card border border-border shadow-sm relative overflow-hidden" data-testid="result-hero-card-category">
            <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-terracotta-100/50 blur-3xl" aria-hidden />
            <div className="relative grid md:grid-cols-5 gap-6 md:gap-8 items-center">
              <div className="md:col-span-2 flex flex-col items-start gap-3">
                {(() => {
                  const rd = inst?.result_display_config || {};
                  const badge = lang === "id" ? (rd.badge_text_id || rd.badge_text_en) : (rd.badge_text_en || rd.badge_text_id);
                  const catLabel = inst?.category ? ((lang === "en" ? inst.category.title_en : inst.category.title_id) || inst.category.title_id) : "";
                  const label = badge || catLabel || pick(inst || {}, "name");
                  if (!label) return null;
                  return (
                    <span className="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-semibold px-3 py-1.5 rounded-full bg-terracotta-100 text-terracotta-700 uppercase tracking-widest" data-testid="result-badge">
                      <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />{label}
                    </span>
                  );
                })()}
                <div className="text-[11px] md:text-xs uppercase tracking-widest text-muted-foreground">
                  {lang === "id" ? "Tingkat kamu:" : "Your level:"}
                </div>
                <h2 className="font-display text-3xl md:text-5xl leading-tight text-teal-800 break-words" data-testid="result-category-level">
                  {(lang === "id" ? categoryResult.label_id : categoryResult.label_en) || categoryResult.label_id || categoryResult.code}
                </h2>
                <div className="mt-1">
                  <ResultIllustration src={categoryResult.illustration_url} alt={categoryResult.label_id || categoryResult.code} size="lg" />
                </div>
              </div>
              <div className="md:col-span-3 space-y-4">
                <div className="p-4 rounded-2xl bg-cream-100/70 border border-terracotta-200/70" data-testid="result-category-score">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-display text-3xl md:text-4xl text-terracotta-700 tabular-nums">{categoryResult.total_score}</span>
                    {categoryResult.total_percentage != null && (
                      <span className="text-sm text-muted-foreground">({categoryResult.total_percentage}%)</span>
                    )}
                    <span className="font-display text-lg md:text-xl text-teal-800 break-words ml-2">
                      {lang === "id" ? "Skor Total" : "Total Score"}
                    </span>
                  </div>
                  {(categoryResult.description_id || categoryResult.description_en) && (
                    <div className="mt-3 text-sm text-teal-800/85 leading-relaxed break-words whitespace-pre-line">
                      {lang === "id" ? (categoryResult.description_id || categoryResult.description_en) : (categoryResult.description_en || categoryResult.description_id)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="relative mt-6 flex items-center gap-2 text-xs text-muted-foreground">
              <span>{t("result.code")}:</span>
              <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/70 hover:bg-secondary font-mono text-[11px] tracking-wider" data-testid="result-code">
                {code} <Copy className="w-3 h-3" />
              </button>
            </div>
          </section>
        ) : !suppressLegacyHero && primary && (
          <section
            className="mb-8 p-5 md:p-8 rounded-3xl bg-card border border-border shadow-sm relative overflow-hidden"
            data-testid="result-hero-card"
          >
            <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-terracotta-100/50 blur-3xl" aria-hidden />
            <div className="relative grid md:grid-cols-5 gap-6 md:gap-8 items-center">
              {/* Left: illustration + badge + dominant name */}
              <div className="md:col-span-2 flex flex-col items-start gap-3">
                {(() => {
                  const rd = inst?.result_display_config || {};
                  const badge = lang === "id" ? (rd.badge_text_id || rd.badge_text_en) : (rd.badge_text_en || rd.badge_text_id);
                  const catLabel = inst?.category ? ((lang === "en" ? inst.category.title_en : inst.category.title_id) || inst.category.title_id) : "";
                  const label = badge || catLabel || pick(inst || {}, "name");
                  if (!label) return null;
                  return (
                    <span className="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-semibold px-3 py-1.5 rounded-full bg-terracotta-100 text-terracotta-700 uppercase tracking-widest" data-testid="result-badge">
                      <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                      {label}
                    </span>
                  );
                })()}
                <div className="text-[11px] md:text-xs uppercase tracking-widest text-muted-foreground">
                  {isRankingMethod
                    ? (lang === "id" ? "Peringkat teratas kamu:" : "Your top rank:")
                    : (lang === "id" ? "Profil dominan kamu:" : "Your dominant profile:")}
                </div>
                <h2 className="font-display text-3xl md:text-5xl leading-tight text-teal-800 break-words" data-testid="result-dominant">
                  {tie === "tie_top2" && secondary
                    ? `${primary[`name_${lang}`] || primary.name_id} · ${secondary[`name_${lang}`] || secondary.name_id}`
                    : (primary[`name_${lang}`] || primary.name_id)}
                </h2>
                <div className="mt-1">
                  <ResultIllustration
                    alt={primary[`name_${lang}`] || primary.name_id}
                    size="lg"
                  />
                </div>
              </div>

              {/* Right: breakdown chart (type set by admin chart_config) */}
              <div className="md:col-span-3">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="font-display text-lg md:text-xl">{lang === "id" ? "Profil Lengkap Kamu" : "Your Full Profile"}</div>
                  <div className="text-[11px] text-muted-foreground">{lang === "id" ? "Persentase per profil" : "Percentage per profile"}</div>
                </div>
                <div data-testid="result-breakdown">
                  <ResultChart
                    dimensions={chartDims}
                    type={chartCfg.type || "bar_horizontal"}
                    lang={lang}
                    showPercentage={chartCfg.show_percentage !== false}
                    showRawScore={!!chartCfg.show_raw_score}
                    showTopOnly={!!chartCfg.show_top_only}
                    topN={chartCfg.top_n || 3}
                  />
                </div>

                {/* Dominant highlight card inside the hero */}
                <div className="mt-5 p-4 rounded-2xl bg-cream-100/70 border border-terracotta-200/70" data-testid="result-dominant-card">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-3xl md:text-4xl text-terracotta-700 tabular-nums">
                      {Math.round(primary.percentage ?? 0)}%
                    </span>
                    <span className="font-display text-lg md:text-xl text-teal-800 break-words">
                      {primary[`name_${lang}`] || primary.name_id}
                    </span>
                  </div>
                  {(primary[`description_${lang}`] || primary.description_id) && (
                    <div className="mt-2 text-sm text-teal-800/85 leading-relaxed break-words">
                      <HtmlContent html={primary[`description_${lang}`] || primary.description_id} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Anonymous code chip */}
            <div className="relative mt-6 flex items-center gap-2 text-xs text-muted-foreground">
              <span>{t("result.code")}:</span>
              <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/70 hover:bg-secondary font-mono text-[11px] tracking-wider" data-testid="result-code">
                {code} <Copy className="w-3 h-3" />
              </button>
              {template.show_total && (
                <span className="ml-3">{t("result.total")}: <span className="font-semibold text-teal-800">{r.total_score}</span></span>
              )}
            </div>
          </section>
        )}

        {/* Big Five / type code without a Hero Card for this result yet: keep the code chip and summary. */}
        {!paidContentLocked && !useUniversalHero && ownProfileView && (
          <section className="mb-6 p-5 md:p-6 rounded-3xl bg-card border border-border" data-testid="big-five-hero-fallback">
            {bigFive && <BigFiveHighlightBadge bigFive={bigFive} lang={lang} />}
            {typeCode && <TypeCodeBadge typeCode={typeCode} lang={lang} />}
            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="result-anon-code">
              <span>{t("result.code")}:</span>
              <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/70 hover:bg-secondary font-mono text-[11px] tracking-wider" data-testid="result-code">
                {code} <Copy className="w-3 h-3" />
              </button>
            </div>
          </section>
        )}

        {paidContentLocked && !showSafeResultHero && (
          <div className="mb-6 flex items-center gap-2 text-xs text-muted-foreground" data-testid="result-paid-code-only">
            <span>{t("result.code")}:</span>
            <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/70 hover:bg-secondary font-mono text-[11px] tracking-wider" data-testid="result-code">
              {code} <Copy className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Learn More uses only backend-allowlisted mapping content and respects
            result_layout.show_reflection for free, entitled, and unpaid paid results. */}
        {showLearnMore && (
          <details className="mb-8 rounded-2xl border border-border bg-card overflow-hidden group" data-testid="result-learn-more">
            <summary className="flex items-center justify-between gap-3 p-5 cursor-pointer list-none select-none hover:bg-secondary/40 transition">
              <div className="flex items-center gap-2">
                <span className="font-display text-base md:text-lg text-teal-800">{lang === "id" ? "Pelajari Lebih Lanjut" : "Learn More"}</span>
                {hasHeroTitle && <span className="text-xs text-muted-foreground truncate max-w-[16rem] md:max-w-none">— {heroTitle}</span>}
              </div>
              <span className="w-8 h-8 rounded-full bg-secondary/60 flex items-center justify-center group-open:rotate-180 transition-transform">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </summary>
            <div className="p-5 md:p-6 border-t border-border space-y-4" data-testid="result-learn-more-content">
              {hasHeroTitle && (
                <h3 className="font-display text-2xl md:text-3xl text-teal-800 break-words" data-testid="lm-title">{heroTitle}</h3>
              )}
              {hasHeroSubtitle && (
                <p className="text-sm md:text-base text-muted-foreground break-words" data-testid="lm-subtitle">{heroSubtitle}</p>
              )}
              {hasHeroReflection && (
                <div className="text-sm md:text-base text-teal-800/90 leading-relaxed" data-testid="lm-reflection">
                  <HtmlContent html={heroReflection} />
                </div>
              )}
              {learnMoreExtras.length > 0 && (
                <div className="grid md:grid-cols-2 gap-4 pt-2" data-testid="lm-extras">
                  {learnMoreExtras.map((section) => (
                    <div key={section.key} className="p-4 rounded-xl bg-secondary/30 border border-border" data-testid={`lm-extra-${section.key}`}>
                      <div className="font-display text-sm md:text-base text-teal-800 mb-2">{section.label}</div>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-teal-800/90">
                        {section.items.map((item) => <li key={item} className="break-words">{item}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </details>
        )}

        {/* Holland (RIASEC) instruments replace the generic chart with the hexagon profile */}
        {useUniversalHero && layout.show_graph && showHollandHexagon && (
          <HollandProfileSection holland={holland} lang={lang} />
        )}

        {showBigFiveMap && <BigFiveProfileSection bigFive={bigFive} lang={lang} />}
        {showTypeCodeMap && <TypeCodeProfileSection typeCode={typeCode} lang={lang} />}

        {/* Standalone chart section — shown when Universal Hero is used AND layout.show_graph enabled */}
        {useUniversalHero && layout.show_graph && !showHollandHexagon && !ownProfileView && chartDims.length > 0 && premiumContentVisible && (
          <section className="mb-8 p-5 md:p-6 rounded-3xl bg-card border border-border" data-testid="result-graph-section">
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-display text-lg md:text-xl">{lang === "id" ? "Grafik Profil" : "Profile Chart"}</div>
              <div className="text-[11px] text-muted-foreground">{lang === "id" ? "Persentase tiap aspek" : "Percentage by aspect"}</div>
            </div>
            <ResultChart
              dimensions={chartDims}
              type={chartCfg.type || "bar_horizontal"}
              lang={lang}
              showPercentage={chartCfg.show_percentage !== false}
              showRawScore={!!chartCfg.show_raw_score}
              showTopOnly={!!chartCfg.show_top_only}
              topN={chartCfg.top_n || 3}
            />
          </section>
        )}

        {/* Combined Dimensions (method="combined_dimension") — derived formula-based scores */}
        {layout.show_combined && combinedScores.length > 0 && premiumContentVisible && (
          <section className="mb-8" data-testid="result-combined-scores">
            <h2 className="font-display text-2xl mb-4">{lang === "id" ? "Skor Gabungan" : "Combined Scores"}</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {combinedScores.map((cs) => (
                <div key={cs.id || cs.code} className="p-5 rounded-2xl bg-card border border-border" data-testid={`combined-score-${cs.code}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-display text-lg text-teal-800 break-words">{cs[`name_${lang}`] || cs.name_id || cs.code}</div>
                      {(cs[`description_${lang}`] || cs.description_id) && (
                        <div className="text-xs text-muted-foreground break-words">{cs[`description_${lang}`] || cs.description_id}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-2xl text-terracotta-700 tabular-nums">{cs.value}</div>
                      <div className="text-[10px] uppercase text-muted-foreground tracking-widest">{cs.percentage}%</div>
                    </div>
                  </div>
                  <Progress value={cs.percentage} className="h-1.5 mt-3" />
                  <div className="text-[10px] font-mono text-muted-foreground mt-2 break-all">{cs.formula}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Share CTA (below the hero) */}
        <div className="mb-8 p-5 rounded-2xl bg-gradient-to-br from-terracotta-50 to-cream-100 border border-terracotta-200/60 flex flex-wrap items-center justify-between gap-3" data-testid="result-share-cta">
          <div className="min-w-0">
            <div className="font-display text-lg text-teal-800">{lang === "id" ? "Yuk, bagikan hasil kamu!" : "Share your result!"}</div>
            <div className="text-xs text-muted-foreground max-w-lg break-words">
              {lang === "id"
                ? "Salin tautan hasil untuk dibagikan, atau unduh kartu hasil untuk diposting di media sosial."
                : "Copy the link to share your result, or download a result card to post on social media."}
            </div>
          </div>
          {isPreview ? <PreviewShareButton lang={lang} /> : (
            <ShareSheet
              code={code}
              instrument={inst}
              primary={primary}
              lang={lang}
              publicShareEnabled={!!shareCfg.public_share_enabled}
              accessToken={unlocked ? accessToken : ""}
            />
          )}
        </div>

        {/* (Peta Profil section removed — chart is now embedded inside the hero card above) */}

        {/* Paid mode gate — hides all detailed sections until unlock */}
        {isPaid && !unlocked && (
          <div className="mb-8 p-6 rounded-2xl border border-terracotta-200 bg-terracotta-50/70" data-testid="paid-report-gate">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-terracotta-100 text-terracotta-700 flex items-center justify-center">🔒</div>
              <div className="min-w-0">
                <div className="font-display text-lg text-teal-800">{lang === "id" ? "Laporan Lengkap Terkunci" : "Full Report Locked"}</div>
                <div className="text-xs text-muted-foreground break-words">{lang === "id" ? "Dapatkan interpretasi mendalam, skor semua dimensi, dan narasi reflektif dalam PDF." : "Get the in-depth interpretation, scores for every aspect, and a reflective narrative in a PDF."}</div>
              </div>
            </div>
            {pdfMode?.price ? (
              <div className="mt-2 text-2xl font-display text-terracotta-700" data-testid="paid-report-price">
                {new Intl.NumberFormat(lang === "id" ? "id-ID" : "en-US", { style: "currency", currency: (pdfMode.currency || "IDR").toUpperCase() }).format(pdfMode.price)}
              </div>
            ) : null}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button onClick={startCheckout} disabled={checkingOut || isPreview} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid="paid-report-unlock-btn">
                {checkingOut ? "…" : (pdfMode?.button_text || (lang === "id" ? "Buka Laporan Lengkap" : "Unlock Full Report"))}
              </Button>
              {pdfMode?.support_contact && (
                <a href={`mailto:${pdfMode.support_contact}`} className="text-xs text-muted-foreground underline underline-offset-4">{lang === "id" ? "Butuh bantuan?" : "Need help?"}</a>
              )}
            </div>
          </div>
        )}

        {isPaid && unlocked && (
          <div className="mb-8 p-4 rounded-2xl border border-emerald-200 bg-emerald-50/80 flex flex-wrap items-center gap-3" data-testid="paid-report-unlocked">
            <div className="text-sm text-emerald-800 min-w-0 flex-1 break-words">{pdfMode?.success_message || (lang === "id" ? "Pembayaran berhasil. Laporan lengkapmu sudah bisa diunduh." : "Payment confirmed. Your full report is ready.")}</div>
            <Button onClick={openPdf} disabled={isPreview} className="rounded-full bg-teal-800 hover:bg-teal-700 text-cream-50" data-testid="paid-report-open-pdf-btn">{lang === "id" ? "Buka PDF" : "Open PDF"}</Button>
          </div>
        )}

        {/* AI narrative (Fase 3) — free-tier hidden in paid mode */}
        {inst?.ai_config?.enabled && premiumContentVisible && (
          <AiNarrativeCard code={code} lang={lang} initialText={data.ai_narrative?.text} accessToken={accessToken} preview={isPreview} />
        )}

        {/* Custom report sections (Fase 3) — hidden in paid mode until unlock */}
        {template.report_sections?.length > 0 && premiumContentVisible && (
          <div className="mb-8 space-y-4" data-testid="report-sections">
            {template.report_sections.map((sec, i) => {
              const title = lang === "en" ? (sec.title_en || sec.title_id) : (sec.title_id || sec.title_en);
              const body = lang === "en" ? (sec.body_en || sec.body_id) : (sec.body_id || sec.body_en);
              if (!body) return null;
              return (
                <div key={sec.key || i} className="p-5 rounded-2xl bg-card border border-border" data-testid={`report-section-${sec.key || i}`}>
                  {title && <div className="font-display text-lg mb-2 break-words">{title}</div>}
                  <div className="text-sm leading-relaxed"><HtmlContent html={body} /></div>
                </div>
              );
            })}
          </div>
        )}

        {/* Ranked list — layout.show_ranking toggle */}
        {isDominant && layout.show_ranking && r.rank_order?.length > 0 && premiumContentVisible && (
          <div className="mb-10" data-testid="result-ranking-section">
            <h2 className="font-display text-2xl mb-4">
              {isRankingMethod
                ? (lang === "id" ? "Peringkat Lengkap" : "Full Ranking")
                : (lang === "id" ? "Urutan Profil" : "Ranked Profiles")}
            </h2>
            <div className="space-y-2" data-testid="result-rank-list">
              {r.rank_order.map((d) => (
                <div key={d.dimension_id} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border" data-testid={`rank-row-${d.code}`}>
                  <div className="w-8 h-8 rounded-full bg-terracotta-100 text-terracotta-700 flex items-center justify-center font-display text-sm shrink-0">{d.rank}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm break-words">{d[`name_${lang}`] || d.name_id}</div>
                    <div className="text-xs text-muted-foreground">{d.raw_score} / {d.max_possible}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-lg">{d.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {layout.show_dimensions && !ownProfileView && r.dimensions?.length > 0 && premiumContentVisible && (
          <div className="mb-10">
            <h2 className="font-display text-2xl mb-4">{t("result.dimensions")}</h2>
            <div className="space-y-3">
              {r.dimensions.map((d) => {
                const percent = d.percentage ?? (d.max_possible > 0 ? (d.raw_score / d.max_possible) * 100 : 0);
                const categoryLabel = d.category ? (d.category[`label_${lang}`] || d.category.label_id) : null;
                return (
                  <div key={d.dimension_id} className="p-5 rounded-2xl bg-card border border-border" data-testid={`dimension-${d.code}`}>
                    <div className="flex items-center gap-4 sm:gap-5">
                      <PercentRing value={percent} testId={`dimension-percent-${d.code}`} />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <div className="font-display text-lg break-words">{d[`name_${lang}`] || d.name_id}</div>
                          {categoryLabel && (
                            <span className="px-2.5 py-0.5 rounded-full bg-terracotta-50 text-terracotta-700 border border-terracotta-200 text-[11px] font-semibold uppercase tracking-wider break-words">{categoryLabel}</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground break-words mt-0.5">{d[`description_${lang}`] || d.description_id}</div>
                      </div>
                    </div>
                    {d.norm && (
                      <div className="mt-3 flex items-center gap-3 text-xs">
                        <span className="px-2 py-0.5 rounded-full bg-sage-100 text-teal-800 border border-sage-200 uppercase tracking-wider">T={d.norm.t_score}</span>
                        <span className="text-muted-foreground">z={d.norm.z}</span>
                        <span className="text-muted-foreground capitalize">· {(d.norm.band || "").replace("_", " ")}</span>
                      </div>
                    )}
                    {d.category && (d.category[`interpretation_${lang}`] || d.category.interpretation_id || d.category[`recommendation_${lang}`] || d.category.recommendation_id) && (
                      <div className="mt-4 p-3 rounded-lg bg-secondary/60 border border-border">
                        <div className="text-sm leading-relaxed"><HtmlContent html={d.category[`interpretation_${lang}`] || d.category.interpretation_id} /></div>
                        {(d.category[`recommendation_${lang}`] || d.category.recommendation_id) && (
                          <div className="text-xs text-muted-foreground mt-2 italic"><HtmlContent html={d.category[`recommendation_${lang}`] || d.category.recommendation_id} /></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {pick(template, "footer") && (
          <div className="p-5 rounded-xl bg-cream-100 border border-border text-sm leading-relaxed text-foreground/80"><HtmlContent html={pick(template, "footer")} /></div>
        )}
        <div className="mt-6 p-5 rounded-xl border border-terracotta-200 bg-terracotta-50/50 text-sm text-teal-800">
          <strong>Disclaimer.</strong> <span data-wrap="responsive">{pick(inst || {}, "disclaimer")}</span>
        </div>
      </div>

      {!isPreview && <NextTests currentSlug={inst?.slug} />}
      {!isPreview && <FollowMindovia />}

      <div className="mt-10 flex flex-wrap items-center gap-3 print:hidden">
        {!isPreview && (
          <Link to={localizedPath("/instruments", lang)} className="ml-auto">
            <Button variant="ghost" data-testid="back-catalogue-btn">{t("result.back")}</Button>
          </Link>
        )}
      </div>
    </div>
  );
}
